import { useMutation, useQuery, useQueryClient } from 'react-query'
import { gridClient, headers } from '@/utils/apiUtil'
import type { AutoPopulateItemResult } from '@/components/auto-populate/types'
import type { CustomDomainResponse } from '@/container/workflow-application-edit/api'

/** The image assets, sent as files to `PATCH /customer/:id/branding/image`. */
export const BRANDING_IMAGE_FIELDS = ['logo', 'logoDark', 'favicon'] as const

/** The plain values, sent as JSON to `PATCH /customer/:id/branding`. */
export const BRANDING_CONFIG_FIELDS = ['companyDisplayName', 'primaryColor', 'primaryColorDark', 'font', 'borderRadiusPx', 'autoPopulatedFrom'] as const

/** Registered with the hosting platform through its own routes, so it is never published. */
export const BRANDING_DOMAIN_FIELD = 'customDomain'

export type BrandingImageField = typeof BRANDING_IMAGE_FIELDS[number]
export type BrandingConfigField = typeof BRANDING_CONFIG_FIELDS[number]
export type BrandingField = BrandingImageField | BrandingConfigField | typeof BRANDING_DOMAIN_FIELD

/**
 * A customer's white-label branding, as `customer.branding` on `GET /customer/:id`.
 * Images are file ids, read with {@link getBrandingImageRequest}.
 */
export type CustomerBranding = {
  logo?: string | null
  logoDark?: string | null
  favicon?: string | null
  faviconDark?: string | null
  primaryColor?: string | null
  primaryColorDark?: string | null
  secondaryColor?: string | null
  secondaryColorDark?: string | null
  font?: string | null
  borderRadiusPx?: number | null
  customDomain?: string | null
  subdomain?: string | null
  /** Shown in place of Grid across the console, e.g. in browser tab titles. */
  companyDisplayName?: string | null
  /** Server-resolved name for tab titles: the display name, else the customer's or its parent org's name. */
  displayName?: string | null
  /** Hostname of the website the published branding was auto-populated from. */
  autoPopulatedFrom?: string | null
}

export type BrandingConfigPayload = Partial<Record<BrandingField, string | number | null>>
export type BrandingImagesPayload = Partial<Record<BrandingImageField, File>>

export const CUSTOMER_BRANDING_QUERY_KEY = 'customer-branding'
export const PUBLIC_BRANDING_QUERY_KEY = 'public-branding'

export const BRANDING_IMAGE_QUERY_KEY = 'branding-image'
export const BRANDING_DOMAIN_QUERY_KEY = 'branding-domain'

/**
 * Reads a branding image as a data URI. The plain file route answers with
 * `Cross-Origin-Resource-Policy: same-origin`, so an `<img>` on the console's origin is
 * refused it; the base64 route comes back through CORS like any other request.
 * @param fileId - id the branding stores for the image
 * @returns a `data:` URI for an `<img>` or an icon link
 */
export const getBrandingImageRequest = async (fileId: string): Promise<string> => {
  const { data } = await gridClient.get(`/customer/file/${fileId}/base64`, { responseType: 'text' })
  return data
}

/**
 * Reads the customer's saved branding.
 * @param customerId - customer to read
 * @returns the branding, or null when the customer has never saved one
 */
export const getCustomerBrandingRequest = async (customerId: string): Promise<CustomerBranding | null> => {
  const { data } = await gridClient.get(`/customer/${customerId}`)
  return data?.branding ?? null
}

/**
 * Reads the branding a reseller serves on a host, for pages with no signed-in user.
 * @param host - hostname the console is served on
 * @returns the branding for that host
 */
export const getPublicBrandingRequest = async (host: string): Promise<CustomerBranding> => {
  const { data } = await gridClient.get('/customer/branding/public', { params: { host } })
  return data
}

export const SSO_BRANDING_QUERY_KEY = 'sso-branding'

/**
 * Reads the white-label branding named by an SSO callback's brand token.
 * @param token - the `brand` query value Grid adds to the SSO callback
 * @returns the branding, or null when the token names none
 */
export const getSsoBrandingRequest = async (token: string): Promise<CustomerBranding | null> => {
  const { data } = await gridClient.get('/auth/sso/branding', { params: { token } })
  return data || null
}

/**
 * Saves branding values. `null` asks the server to clear a field.
 * @param customerId - customer to update
 * @param payload - the fields to change
 * @returns the updated customer's branding
 */
export const updateBrandingConfigRequest = async (customerId: string, payload: BrandingConfigPayload) => {
  const { data } = await gridClient.patch(`/customer/${customerId}/branding`, payload)
  return (data?.branding ?? null) as CustomerBranding | null
}

/**
 * Uploads branding images.
 * @param customerId - customer to update
 * @param images - one file per image field to replace
 * @returns the updated customer's branding
 */
export const updateBrandingImagesRequest = async (customerId: string, images: BrandingImagesPayload) => {
  const formData = new FormData()
  Object.entries(images).forEach(([field, file]) => formData.append(field, file))

  const { data } = await gridClient.patch(`/customer/${customerId}/branding/image`, formData, {
    headers: { ...headers(), 'Content-Type': 'multipart/form-data' },
  })
  return (data?.branding ?? null) as CustomerBranding | null
}

/** The branding fields auto-populate can fill from a website. */
export const BRANDING_AUTO_POPULATE_FIELDS = ['logo', 'logoDark', 'favicon', 'companyDisplayName', 'primaryColor', 'primaryColorDark', 'font', 'borderRadiusPx'] as const

export type BrandingAutoPopulateField = typeof BRANDING_AUTO_POPULATE_FIELDS[number]

export type BrandingAutoPopulateImage = {
  base64: string
  mimeType: string
  fileName: string
}

export type BrandingAutoPopulateResponse = {
  results: AutoPopulateItemResult<BrandingAutoPopulateField>[]
  autoPopulatedFrom?: string
  images: Partial<Record<'logo' | 'logoDark' | 'favicon', BrandingAutoPopulateImage>>
}

/**
 * Reads a website for branding suggestions. Nothing is saved: the results are meant for the draft.
 * @param customerId - customer whose branding is drafted
 * @param websiteUrl - the website as typed
 * @returns one result per field, and the logo and favicon as base64 PNGs when found
 */
export const autoPopulateBrandingRequest = async (customerId: string, websiteUrl: string): Promise<BrandingAutoPopulateResponse> => {
  const { data } = await gridClient.post(`/customer/${customerId}/branding/auto-populate`, { websiteUrl })
  return data
}

/**
 * @param customerId - customer whose branding is drafted
 * @returns the react-query mutation for {@link autoPopulateBrandingRequest}
 */
export function useAutoPopulateBranding(customerId: string) {
  return useMutation((websiteUrl: string) => autoPopulateBrandingRequest(customerId, websiteUrl))
}

/**
 * Registers the console domain with the hosting platform. It takes effect at once, without publishing.
 * @param customerId - customer whose console domain is added
 * @param domainName - the bare host
 * @returns the platform's record, with the CNAME target to add to DNS
 */
export const addBrandingDomainRequest = async (customerId: string, domainName: string): Promise<CustomDomainResponse> => {
  const { data } = await gridClient.post(`/customer/${customerId}/branding/custom-domain`, { domainName })
  return data
}

/**
 * @param customerId - customer whose console domain is read
 * @returns the platform's current record for the domain
 */
export const getBrandingDomainRequest = async (customerId: string): Promise<CustomDomainResponse> => {
  const { data } = await gridClient.get(`/customer/${customerId}/branding/custom-domain`)
  return data
}

/**
 * Asks the platform to retry a certificate it gave up on, e.g. once DNS was fixed.
 * @param customerId - customer whose console domain is retried
 * @returns the domain's record after the retry
 */
export const retryBrandingDomainRequest = async (customerId: string): Promise<CustomDomainResponse> => {
  const { data } = await gridClient.post(`/customer/${customerId}/branding/custom-domain/retry`)
  return data
}

/**
 * @param customerId - customer whose console domain is removed from the platform and the branding
 */
export const deleteBrandingDomainRequest = async (customerId: string): Promise<void> => {
  await gridClient.delete(`/customer/${customerId}/branding/custom-domain`)
}

/**
 * @param customerId - customer whose console domain is read
 * @param options - `enabled` holds the request back until the customer has a domain
 * @returns the react-query result
 */
export function useBrandingDomainStatus(customerId: string, { enabled = true } = {}) {
  return useQuery(
    [BRANDING_DOMAIN_QUERY_KEY, customerId],
    () => getBrandingDomainRequest(customerId),
    { refetchOnWindowFocus: false, retry: false, enabled: enabled && !!customerId },
  )
}

/**
 * Adding or removing the domain changes the saved branding, so both queries are refreshed.
 * @param customerId - customer whose console domain is managed
 * @returns the add, retry and delete mutations
 */
export function useBrandingDomainMutations(customerId: string) {
  const queryClient = useQueryClient()
  // Written straight into the cached branding so the card and DNS modal don't wait on the refetch.
  const setSavedDomain = (hostname: string | null) => queryClient.setQueryData<CustomerBranding | null | undefined>(
    [CUSTOMER_BRANDING_QUERY_KEY, customerId],
    branding => ({ ...branding, [BRANDING_DOMAIN_FIELD]: hostname }),
  )
  const add = useMutation((domainName: string) => addBrandingDomainRequest(customerId, domainName), {
    onSuccess: domain => {
      queryClient.setQueryData([BRANDING_DOMAIN_QUERY_KEY, customerId], domain)
      setSavedDomain(domain.hostname)
      queryClient.invalidateQueries([CUSTOMER_BRANDING_QUERY_KEY, customerId])
    },
  })
  const remove = useMutation(() => deleteBrandingDomainRequest(customerId), {
    onSuccess: () => {
      setSavedDomain(null)
      queryClient.removeQueries([BRANDING_DOMAIN_QUERY_KEY, customerId])
      queryClient.invalidateQueries([CUSTOMER_BRANDING_QUERY_KEY, customerId])
    },
  })
  const retry = useMutation(() => retryBrandingDomainRequest(customerId), {
    onSuccess: domain => {
      queryClient.setQueryData([BRANDING_DOMAIN_QUERY_KEY, customerId], domain)
    },
  })
  return { add, retry, remove }
}

/**
 * The customer's saved branding, shared by the subtab and the app theme.
 * @param customerId - customer to read
 * @param options - `enabled` holds the request back
 * @returns the react-query result
 */
export function useCustomerBranding(customerId: string | undefined, { enabled = true } = {}) {
  return useQuery(
    [CUSTOMER_BRANDING_QUERY_KEY, customerId],
    () => getCustomerBrandingRequest(customerId as string),
    { refetchOnWindowFocus: false, enabled: enabled && !!customerId },
  )
}

/**
 * The branding a reseller serves on a host. Fetched once: it changes only when published.
 * @param host - hostname the console is served on
 * @param options - `enabled` holds the request back
 * @returns the react-query result
 */
export function usePublicBranding(host: string, { enabled = true } = {}) {
  return useQuery(
    [PUBLIC_BRANDING_QUERY_KEY, host],
    () => getPublicBrandingRequest(host),
    {
      refetchOnWindowFocus: false, retry: false, staleTime: Infinity, enabled,
    },
  )
}

/**
 * The branding an SSO callback's brand token names. Fetched once per token.
 * @param token - the `brand` query value on the SSO callback
 * @param options - `enabled` holds the request back
 * @returns the react-query result
 */
export function useSsoBranding(token: string | null, { enabled = true } = {}) {
  return useQuery(
    [SSO_BRANDING_QUERY_KEY, token],
    () => getSsoBrandingRequest(token as string),
    {
      refetchOnWindowFocus: false, retry: false, staleTime: Infinity, enabled: enabled && !!token,
    },
  )
}

/**
 * A branding image as a data URI, fetched once per file id and shared by every place
 * that shows it.
 * @param fileId - id the branding stores for the image
 * @returns the react-query result
 */
export function useBrandingImage(fileId?: string | null) {
  return useQuery(
    [BRANDING_IMAGE_QUERY_KEY, fileId],
    () => getBrandingImageRequest(fileId as string),
    {
      refetchOnWindowFocus: false, retry: false, staleTime: Infinity, enabled: !!fileId,
    },
  )
}
